/**
 * Offer countdown formatting for the store app.
 *
 * The countdown is the whole point of the accept-or-retarget feature, so it is
 * a pure function with its own tests rather than inline JSX arithmetic.
 *
 * Urgency thresholds (from the UI design): amber under 6h, red under 1h.
 */
export type CountdownUrgency = "normal" | "soon" | "urgent" | "expired";

export interface Countdown {
	label: string;
	urgency: CountdownUrgency;
}

export function formatCountdown(
	expiresAt: string | null | undefined,
	now: number = Date.now(),
): Countdown {
	if (!expiresAt) return { label: "no deadline", urgency: "normal" };

	const ms = new Date(expiresAt).getTime() - now;
	if (Number.isNaN(ms)) return { label: "no deadline", urgency: "normal" };
	if (ms <= 0) return { label: "expired", urgency: "expired" };

	const totalMinutes = Math.floor(ms / 60_000);
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	const label = hours > 0 ? `expires in ${hours}h ${minutes}m` : `expires in ${minutes}m`;

	// Thresholds are on the real remaining time, not the rounded label, so a
	// 59m59s offer reads as urgent rather than as "1h".
	const urgency: CountdownUrgency = ms < 3_600_000 ? "urgent" : ms < 21_600_000 ? "soon" : "normal";

	return { label, urgency };
}

/** Tailwind text colour for an urgency level. */
export function countdownClass(urgency: CountdownUrgency): string {
	switch (urgency) {
		case "urgent":
			return "text-destructive";
		case "soon":
			return "text-amber-600 dark:text-amber-500";
		case "expired":
			return "text-muted-foreground";
		default:
			return "text-muted-foreground";
	}
}
